import { Inject } from '@odinjs/odin';

import { Endpoint, ToastService } from '@philips-emr/hpms-provider-commons';
import { HpmsCustomContentCollapseApi, HpmsCustomContentCollapseEvent, HPMSOnClickActionTitle, HPMSOnOpenCollapse } from '@philips-emr/hpms-provider-components';
import { FetchAPI, FieldAPI, LegendsAPI, PanelAPI } from '@philips-emr/tws-components';
import { ToastLevel } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnBlur, OnCancel, OnClose, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ClickEvent, FetchEvent, MountEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';

import { FragmentNames } from '../../codegen';
import { NegotiationProceduresAliases as Aliases } from '../../codegen/constants/aliases/negotiation-procedures-aliases';
import BaseController from '../BaseController';

interface Item {
  edit: string,
  ieValueCounterProposal: string,
  vlCpProcedure: number,
  vlCpDoctor: number,
  vlCpFilm: number,
  vlCpOperationalCost: number,
  vlCpAnesthetist: number,
  vlCpAssistant: number,
  nrSeqItemProposal: number,
  itemType: string,
}

const PROCEDURE = 'P';

@Controller({ fragment: FragmentNames.NEGOTIATION_PROCEDURES })
class NegotiationProceduresController extends BaseController {

  @Inject(Aliases.FETCH_PROCEDURE)
  private fetchProcedures: FetchAPI;
  @Inject(Aliases.PANEL_MODAL_COUNTER_OFFER)
  private panelModal: PanelAPI;
  @Inject(Aliases.FIELD_CP_PROCEDURE)
  private fieldProcedureValue: FieldAPI;
  @Inject(Aliases.FIELD_CP_OPERATIONAL_COST)
  private fieldOperatinalCost: FieldAPI;
  @Inject(Aliases.FIELD_CP_DOCTOR_VALUE_EDIT)
  private fieldDoctor: FieldAPI;
  @Inject(Aliases.FIELD_CP_FILM_VALUE_EDIT)
  private fieldFilm: FieldAPI;
  @Inject(Aliases.FIELD_CP_AUX_VALUES_EDIT)
  private fieldAuxiliar: FieldAPI;
  @Inject(Aliases.FIELD_CP_ANESTHETIST_EDIT)
  private fieldAnesthetist: FieldAPI;
  @Inject(Aliases.LEGENDS)
  private legends: LegendsAPI;

  @OnMount(Aliases.FETCH_PROCEDURE)
  onMountFetch(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqProposal, nrSeqNegotiation } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqNegotiation, nrSeqProposal, 'itemType': PROCEDURE });
    api.setParameters({ nrSeqNegotiation, nrSeqProposal, 'itemType': PROCEDURE });
    api.forceFetch();
  }

  @OnAfterFetch(Aliases.FETCH_PROCEDURE)
  onAfterFetchProcedures(event: FetchEvent): void {
    const data = event.data as Item[];
    if (!data || data.length === 0) {
      this.legends.setShowExpression('');
      this.legends.setHideExpression('');
    }
  }

  @OnBlur([
    Aliases.FIELD_CP_OPERATIONAL_COST, Aliases.FIELD_CP_DOCTOR_VALUE_EDIT,
    Aliases.FIELD_CP_FILM_VALUE_EDIT, Aliases.FIELD_CP_AUX_VALUES_EDIT,
    Aliases.FIELD_CP_ANESTHETIST_EDIT])
  onChangeValues(): void {
    const { vlCpOperationalCost } = this.fieldOperatinalCost.getData() as unknown as Item;
    const { vlCpAnesthetist } = this.fieldAnesthetist.getData() as unknown as Item;
    const { vlCpDoctor } = this.fieldDoctor.getData() as unknown as Item;
    const { vlCpFilm } = this.fieldFilm.getData() as unknown as Item;
    const { vlCpAssistant } = this.fieldAuxiliar.getData() as unknown as Item;

    const total = vlCpOperationalCost + vlCpAnesthetist + vlCpDoctor + vlCpFilm + vlCpAssistant;
    this.fieldProcedureValue.setValue(total);
  }

  @OnSubmit(Aliases.FORM_COUNTER_OFFER)
  onSubmitProcedure(event: SubmitEvent): void {
    const body = { ...event.record, 'itemType': PROCEDURE };
    const { nrSeqProposal, nrSeqNegotiation } = this.historyService.getCurrentRouteParams();

    HttpService.post(Endpoint.POST.EDIT_NEGOTIATION_PROPOSAL_ITEM, { nrSeqProposal, nrSeqNegotiation }, { body })
      .then(() => {
        this.fetchProcedures.forceFetch();
        this.panelModal.setVisible(false);
        ToastService.open({ message: MessageService.translate('provider.succes-change'), level: ToastLevel.SUCCESS });
      }).catch(() => {
        ToastService.open({ message: MessageService.translate('provider.error-process-request'), level: ToastLevel.ERROR });
      });
  }

  @HPMSOnOpenCollapse([Aliases.PROPOSAL_COLAPSE, Aliases.COUNTER_PROPOSAL_COLAPSE])
  onOpenRenderContent(event: HpmsCustomContentCollapseEvent<Item>): void {
    if (!event || !event.data) {
      return;
    }
    const api = event.target as unknown as HpmsCustomContentCollapseApi;
    const { data, onOpenRender } = event;
    const { edit, nrSeqItemProposal } = data;
    const { nrSeqProposal, nrSeqNegotiation } = this.historyService.getCurrentRouteParams();
    api.setHasTitleAction(edit == 'S');
    onOpenRender({ parameters: { nrSeqProposal, nrSeqNegotiation, itemType: data.itemType, nrSeqItemProposal } });
  }

  @HPMSOnClickActionTitle(Aliases.COUNTER_PROPOSAL_COLAPSE)
  onClickActionTitle(event: ClickEvent): void {
    const nrSeqItemProposal = event.value;
    this.context.set({ nrSeqItemProposal });
    this.panelModal.setVisible(true);
  }

  @OnClose(Aliases.MODAL_COUNTER_PROPOSAL)
  onCloseModal(): void {
    this.panelModal.setVisible(false);
  }

  @OnCancel(Aliases.FORM_COUNTER_OFFER)
  onCancelCounterOffer(): void {
    this.panelModal.setVisible(false);
  }

  @OnMount(Aliases.FETCH_COUNT_OFFER)
  onMountFetchCounterOffer(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqProposal, nrSeqNegotiation } = this.historyService.getCurrentRouteParams();
    const { nrSeqItemProposal } = this.context.getBulkData() as unknown as Item;
    api.setParameters({ nrSeqNegotiation, nrSeqProposal, 'itemType': PROCEDURE, nrSeqItemProposal });
    api.forceFetch();
  }

}

export {
  NegotiationProceduresController,
  Item,
};
